import { Fragment } from 'react'
import PropTypes from 'prop-types'
import { useTheme } from '@emotion/react'
import { graphql } from 'gatsby'
import {
  List,
  ListItem,
  Container,
  Section,
  RowColumn,
  Row,
  SectionTitle,
} from 'chaoskit/src/components'
import { tealBrightTeal } from 'chaoskit/src/assets/styles/utility/gradient'
import { MDXRenderer } from 'gatsby-plugin-mdx'

import StyledHeading from '../events/StyledHeading'

import Icon from '~components/Icon'
import Link from '~components/Link'
import Data from '~components/Data'
import EventForm from '~components/EventForm'
import { listBullet } from '~styles/utility'
import BaseLayout from '~layouts/BaseLayout'
import HelmetSEO from '~partials/global/HelmetSEO'
import StaticFeaturedArticles from '~partials/global/StaticFeaturedArticles'
import StaticFooter from '~partials/footer/StaticFooter'
import FooterCTA from '~partials/footer/FooterCTA'
import Hero, { HeroContent, HeroTitle, HeroTitleSub } from '~partials/Hero'
import NodeTypeTracker from '~partials/global/NodeTypeTracker'
import Speakers from '~partials/events/Speakers'
import MutinyTag from '~components/MutintyTag'

const UpcomingWebinar = ({ node }) => {
  const theme = useTheme()

  return (
    <Link
      css={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        borderRadius: theme.borderRadius.base,
        background: theme.color.light.base,
        padding: theme.space.large,
        boxShadow: theme.boxShadow.base,

        '&:hover, &:focus': {
          '.G__Webinars__ActionCard__Icon': {
            transform: `translateX(${theme.space.xsmall}px)`,
          },
        },
      }}
      to={node.fields.fullUrl}
    >
      <div
        css={{
          flex: 1,
          marginBottom: theme.space.large,
        }}
      >
        {(node.frontmatter.onDemand || node.frontmatter.date) && (
          <StyledHeading
            as="h5"
            css={{
              fontSize: theme.fontSize.base,
            }}
          >
            {node.frontmatter.onDemand ? 'On-Demand' : node.frontmatter.date}
          </StyledHeading>
        )}

        <h4
          css={{
            marginTop: theme.space.base,
            lineHeight: theme.lineHeight.small,
          }}
        >
          <Data>{node.frontmatter.hero.heading}</Data>
        </h4>
        <div
          css={{
            fontSize: theme.fontSize.small,
            color: theme.fontColor.base,
          }}
        >
          <Data>{node.excerpt}</Data>
        </div>
      </div>

      <div
        css={{
          fontWeight: theme.fontWeight.bold,
          color: theme.color.primary.base,
          marginTop: 'auto',
        }}
      >
        Read More
        <Icon
          className="G__Webinars__ActionCard__Icon"
          icon="arrow-right"
          css={{
            marginLeft: theme.space.small,
            transition: `transform ${theme.timing.base} ${theme.transition.bounce}`,
          }}
        />
      </div>
    </Link>
  )
}

UpcomingWebinar.propTypes = {
  node: PropTypes.object.isRequired,
}

const WebinarSingle = ({
  data: {
    pageData: {
      body,
      frontmatter: {
        title,
        description,
        socialMediaImage,
        time,
        date,
        dateTime,
        eventSessions,
        onDemand,
        hero: { heading, content, meta, form },
        agenda,
        speakers,
      },
      fields: { slug },
    },
    upcomingWebinars: { edges: upcomingWebinars },
  },
  pageContext: { nodeType },
  location,
}) => {
  const theme = useTheme()

  return (
    <BaseLayout noFooter headerProps={{ sticky: false, showNav: false }}>
      <HelmetSEO
        title={title}
        description={description}
        image={socialMediaImage}
      />
      {location.pathname.includes(
        'introduction-to-chaos-engineering-may18'
      ) && <MutinyTag />}
      <NodeTypeTracker nodeType={nodeType} />
      <Hero contrast={false} css={[tealBrightTeal(theme)]}>
        <HeroContent>
          <Container>
            <Row gutter={{ base: 'base', medium: 'xlarge' }}>
              <RowColumn
                size={{ medium: 6 }}
                css={{
                  textAlign: 'center',

                  [theme.mq.medium]: {
                    textAlign: 'left',
                  },
                }}
                className="u-contrast"
              >
                <div
                  css={{
                    [theme.mq.medium]: {
                      position: 'sticky',
                      top: theme.space.base,
                    },
                  }}
                >
                  <StyledHeading
                    as="h5"
                    css={[
                      theme.fontSize.xlarge__fluid,
                      {
                        color: `${theme.contrast.muted} !important`,
                      },
                    ]}
                  >
                    Webinar
                  </StyledHeading>
                  <HeroTitle as="h2" css={{ marginTop: 0 }}>
                    <Data>{heading}</Data>
                  </HeroTitle>
                  <HeroTitleSub>
                    <MDXRenderer>{content}</MDXRenderer>
                  </HeroTitleSub>
                  {onDemand ? (
                    <div
                      css={{
                        fontWeight: theme.fontWeight.bold,
                        marginTop: theme.space.large,
                        fontSize: theme.fontSize.large,
                      }}
                    >
                      On-demand
                    </div>
                  ) : (
                    date &&
                    time && (
                      <Fragment>
                        <StyledHeading
                          as="h5"
                          css={{
                            marginTop: theme.space.large,
                            marginBottom: theme.space.small,
                            fontSize: theme.fontSize.medium,
                          }}
                        >
                          Time
                        </StyledHeading>
                        <time
                          dateTime={dateTime}
                          itemProp="datePublished"
                          css={{
                            fontWeight: theme.fontWeight.bold,
                            fontSize: theme.fontSize.large,
                          }}
                        >
                          {date}
                          <br />
                          <Data>{time}</Data>
                        </time>
                      </Fragment>
                    )
                  )}
                  {meta && (
                    <div
                      css={{
                        fontSize: theme.fontSize.small,
                        marginTop: theme.space.small,
                      }}
                    >
                      <Data>{meta}</Data>
                    </div>
                  )}
                </div>
              </RowColumn>
              <RowColumn size={{ medium: 6 }}>
                <EventForm
                  formId={slug}
                  onDemand={onDemand}
                  title={form?.heading}
                  sessions={eventSessions}
                  formIdOverride={form?.formIdOverride}
                  disableWorkEmailValidation={form?.disableWorkEmailValidation}
                  fieldValues={{
                    programID: form?.programId,
                    bigMarkerSession: form?.bigMarkerSession,
                  }}
                  variation="webinar"
                />
              </RowColumn>
            </Row>
          </Container>
        </HeroContent>
      </Hero>
      <Section space="xlarge">
        <Container>
          <div
            css={{
              display: 'grid',
              gap: theme.space.large,

              [theme.mq.medium]: {
                gridTemplateColumns: '1fr 2px 1fr',
              },
            }}
          >
            <div>
              <StyledHeading
                as="h5"
                css={{
                  fontSize: theme.fontSize.medium,
                  marginBottom: theme.space.large,
                }}
              >
                About this webinar
              </StyledHeading>
              <div css={[theme.fontSize.large__fluid]}>
                <MDXRenderer>{body}</MDXRenderer>
              </div>
              {agenda?.length > 0 && (
                <Fragment>
                  <StyledHeading
                    as="h5"
                    css={{
                      fontSize: theme.fontSize.medium,
                      marginTop: theme.space.xlarge,
                      marginBottom: theme.space.large,
                    }}
                  >
                    Agenda
                  </StyledHeading>
                  <List space="base" css={listBullet(theme)}>
                    {agenda.map((item) => (
                      <ListItem key={item.content}>
                        <MDXRenderer>{item.content}</MDXRenderer>
                      </ListItem>
                    ))}
                  </List>
                </Fragment>
              )}
            </div>
            <div
              css={{
                background: theme.color.border.base,
                height: 2,
                display: 'block',

                [theme.mq.medium]: {
                  height: 'auto',
                },
              }}
            />
            <div>
              <StyledHeading
                as="h5"
                css={{
                  fontSize: theme.fontSize.medium,
                  marginBottom: theme.space.large,
                }}
              >
                About the speakers
              </StyledHeading>
              <Speakers speakers={speakers} />
            </div>
          </div>
        </Container>
      </Section>
      {upcomingWebinars.length > 0 && (
        <Section css={[tealBrightTeal(theme)]}>
          <Container>
            <SectionTitle
              title="Check out other webinars from Gremlin"
              className="u-contrast"
            />
            <div
              css={{
                display: 'grid',
                gap: theme.space.base,

                [theme.mq.medium]: {
                  gridTemplateColumns: 'repeat(3, 1fr)',
                },
              }}
            >
              {upcomingWebinars.map(({ node }) => (
                <UpcomingWebinar key={node.fileAbsolutePath} node={node} />
              ))}
            </div>
          </Container>
        </Section>
      )}
      <StaticFeaturedArticles
        css={[
          !upcomingWebinars.length && {
            borderTop: theme.border.base,
          },
        ]}
      />
      <FooterCTA />
      <StaticFooter />
    </BaseLayout>
  )
}

WebinarSingle.propTypes = {
  data: PropTypes.object.isRequired,
  pageContext: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
}

export default WebinarSingle

export const pageQuery = graphql`
  query WebinarSingle(
    $slug: String!
    $fileAbsolutePath: String!
    $date: Date!
  ) {
    pageData: mdx(
      fields: { slug: { eq: $slug } }
      fileAbsolutePath: { regex: "/src/webinars/" }
    ) {
      ...WebinarFragment
    }

    upcomingWebinars: allMdx(
      limit: 3
      sort: { fields: [frontmatter___date], order: ASC }
      filter: {
        fileAbsolutePath: { regex: "/src/webinars/", ne: $fileAbsolutePath }
        frontmatter: {
          title: { ne: "BLUEPRINT" }
          hideFromResources: { ne: true }
          date: { gte: $date }
        }
      }
    ) {
      edges {
        node {
          ...WebinarFragment
        }
      }
    }
  }
`
